import React from 'react';
import {
  Box, Container, Flex, Text, Button, HStack, useDisclosure,
  Tooltip, useToast
} from '@chakra-ui/react';
import { FaQuestionCircle, FaBars, FaFileContract, FaHeartbeat } from 'react-icons/fa';
import HelpTicketButton from '../UI/HelpTicketButton';
import { useSelector, useDispatch } from 'react-redux';
import { openAdminPanel } from '../../Redux/Mode/action';
import UserDrawer from '../Profile/UserDrawer';
import LegalModal from '../Legal/LegalModal';
import { useNavigate } from 'react-router-dom';


const Footer = () => {
  const currentYear = new Date().getFullYear();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const legal = useDisclosure(); // Legal modal (Terms / Privacy)
  
  // Check if user is logged in
  const isLoggedIn = useSelector(state => Boolean(
    state.auth?.isAuth && 
    state.auth?.token
  ));
  
  const isAdmin = useSelector(state => 
    state.auth?.isAdmin || 
    state.auth?.role === 'admin' || 
    state.auth?.accountType === 'admin' ||
    state.auth?.user?.role === 'admin' || 
    state.auth?.user?.accountType === 'admin'
  );
  
  // For user settings drawer (same as in navbar)
  const { 
    isOpen: isProfileOpen, 
    onOpen: onProfileOpen, 
    onClose: onProfileClose 
  } = useDisclosure();
  
  // Open admin panel handler with admin check - silently ignore non-admin clicks
  const handleOpenAdminPanel = () => {
    if (isAdmin) {
      dispatch(openAdminPanel());
    }
    // Non-admin clicks are silently ignored - no toast message
  };
  
  // Handle help button click - require sign in
  const handleHelpClick = () => {
    if (isLoggedIn) {
      // Open the help/profile drawer only if logged in
      onProfileOpen();
    } else {
      // Show toast notification and redirect to sign in
      toast({
        title: "Sign in required",
        description: "Please sign in to access help features.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      
      // Navigate to home page (which has sign in)
      navigate('/');
    }
  };
  
  return (
    <Box as="footer" py={4} bg="rgba(0,0,0,0.85)" backdropFilter="blur(16px)" color="white" mt="auto" position="relative" zIndex={10}>
      <Container maxW="container.xl">
        <Flex 
          direction={{ base: 'column', md: 'row' }} 
          justify="space-between" 
          align={{ base: 'center', md: 'center' }}
          wrap="wrap"
        >
          <Flex align="center" mb={{ base: 2, md: 0 }}>
            {/* Admin panel image/button */}
            <Tooltip label={isAdmin ? 'Admin Panel' : ''} placement="top" hasArrow isDisabled={!isAdmin}>
              <Box
                as="button"
                type="button"
                aria-label="Rabbit"
                mr={3}
                title={isAdmin ? 'Admin Panel' : ''}
                onClick={handleOpenAdminPanel}
                _hover={isAdmin ? { opacity: 0.8 } : undefined}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  lineHeight: 0,
                  cursor: isAdmin ? 'pointer' : 'default',
                }}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/media/burrow-solo-white.png`}
                  alt=""
                  style={{ width: '24px', height: '24px', display: 'block', opacity: 0.9 }}
                />
              </Box>
            </Tooltip>
            <Text fontSize="sm">
              © {currentYear} Burrow - Shield of Souls - All Rights Reserved in God
            </Text>
          </Flex>
          
          <HStack spacing={4} wrap="wrap" justify="center">
            <Button
              leftIcon={<FaBars />}
              size="sm"
              variant="ghost"
              color="whiteAlpha.800"
              onClick={() => window.dispatchEvent(new Event('open-modes-drawer'))}
              _hover={{ bg: 'whiteAlpha.200' }}
            >
              Menu
            </Button>
            <HelpTicketButton />
            <Button
              leftIcon={<FaFileContract />}
              size="sm"
              variant="ghost"
              color="whiteAlpha.800"
              onClick={legal.onOpen}
              _hover={{ bg: 'whiteAlpha.200' }}
            >
              Terms
            </Button>
            <Button
              leftIcon={<FaHeartbeat />}
              size="sm"
              variant="ghost"
              color="whiteAlpha.800"
              onClick={() => navigate('/status')}
              _hover={{ bg: 'whiteAlpha.200' }}
            >
              Status
            </Button>
            <Button
              leftIcon={<FaQuestionCircle />}
              size="sm"
              colorScheme="blue"
              variant="ghost"
              onClick={handleHelpClick}
            >
              Help
            </Button>
          </HStack>
        </Flex>
      </Container>
      
      {/* User Settings Drawer */}
      <UserDrawer isOpen={isProfileOpen} onClose={onProfileClose} />
      {/* Legal documents modal */}
      <LegalModal isOpen={legal.isOpen} onClose={legal.onClose} />
    </Box>
  );
};

export default Footer;
